import React, { useRef, useEffect } from 'react';
import { useEditMode } from '@/context/EditModeContext';

/**
 * Renders a value from the homepage config that is inline-editable when
 * edit mode is on. In view mode it renders as a plain <span> (or the given
 * `as` element). In edit mode it becomes a contentEditable target with a
 * dashed outline; blur commits the change to the draft.
 *
 * Props:
 *   path: dotted config path (e.g. "hero.badge" or "threeColumns.0.title")
 *   fallback: value shown when config hasn't loaded
 *   as: HTML tag to render (default 'span')
 *   multiline: keep newlines inside the editable area
 *   className: passed through
 */
export const Editable = ({ path, fallback = '', as = 'span', multiline = false, className = '', ...rest }) => {
  const { enabled, getValue, update } = useEditMode();
  const ref = useRef(null);
  const Tag = as;

  const current = getValue(path);
  const value = (current === undefined || current === null || current === '') ? fallback : current;

  // When entering edit mode, seed the DOM node with the current value
  useEffect(() => {
    if (enabled && ref.current && ref.current.textContent !== String(value ?? '')) {
      ref.current.textContent = String(value ?? '');
    }
  }, [enabled, value]);

  // Stop clicks in edit mode from bubbling to parent Links / buttons
  // that would otherwise steal the click and navigate away.
  const stopIfEditing = (e) => {
    if (enabled) e.stopPropagation();
  };

  if (!enabled) {
    return <Tag className={className} {...rest}>{value}</Tag>;
  }

  return (
    <Tag
      ref={ref}
      className={`${className} outline-dashed outline-2 outline-yellow-400/70 outline-offset-2 rounded-sm cursor-text focus:outline-primary focus:outline-solid`}
      contentEditable
      suppressContentEditableWarning
      spellCheck
      onMouseDown={stopIfEditing}
      onClick={stopIfEditing}
      onPointerDown={stopIfEditing}
      onBlur={(e) => {
        const text = multiline ? e.currentTarget.innerText : e.currentTarget.textContent;
        update(path, text || '');
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (!multiline && e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
      }}
      data-edit-path={path}
      {...rest}
    >
      {value}
    </Tag>
  );
};

export default Editable;
